<template>
  <div class="relative inline-flex items-center">
    <!-- Info icon with hover effect -->
    <button
      @click="toggle"
      @mouseenter="hover = true"
      @mouseleave="hover = false"
      class="p-1 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
      :class="hover ? 'bg-gray-200 bg-opacity-50' : ''"
      aria-label="More information"
    >
      <img
         :src="'/images/icons8-info.svg'"
        alt="Info"
        class="w-5 h-5 cursor-pointer transition-transform duration-200"
        :class="hover ? 'scale-110' : ''"
      />
    </button>

    <!-- Balloon tooltip with pointer -->
    <div
      v-if="visible"
        ref="tooltip"
        class="absolute z-50 min-w-[200px] max-w-[min(90vw,400px)] bg-white border border-gray-200 text-gray-800 text-sm rounded-lg px-4 py-3 shadow-lg transition-all duration-200 balloon-tooltip"
        :class="[positionClass, loading ? 'opacity-75' : '']"
        @click.stop
        >
      <!-- Balloon pointer (triangle) -->
      <div class="absolute w-4 h-4 transform rotate-45 bg-white border-t border-l border-gray-200" :class="pointerClass"></div>
      
      <!-- Close button -->
      <button
        v-if="hasCloseButton"
        @click="visible = false"
        class="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 focus:outline-none"
        aria-label="Close tooltip"
      >
        <svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      
      <!-- Content states -->
      <div v-if="loading" class="flex items-center justify-center space-x-2 py-4">
        <svg class="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Loading...</span>
      </div>
      <div v-else-if="error" class="text-red-500 flex items-center space-x-2 p-2">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ error }}</span>
      </div>
      <div v-else class="prose max-w-none text-gray-700" v-html="tooltipContent"></div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    identifier: {
      type: String,
      required: true
    },
    position: {
      type: String,
      default: 'bottom', // 'top', 'bottom', 'left', 'right'
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    }
  },
  data() {
    return {
      visible: false,
      hover: false,
      loading: false,
      error: null,
      tooltipContent: '',
      hasCloseButton: false
    }
  },
  computed: {
    positionClass() {
      return {
        bottom: 'top-full mt-3 left-1/2 -translate-x-1/2',
        top: 'bottom-full mb-3 left-1/2 -translate-x-1/2',
        left: 'right-full mr-3 top-1/2 -translate-y-1/2',
        right: 'left-full ml-3 top-1/2 -translate-y-1/2'
      }[this.position];
    },
    pointerClass() {
      return {
        bottom: '-top-2 left-1/2 -ml-2 border-t border-l',
        top: '-bottom-2 left-1/2 -ml-2 border-b border-r',
        left: '-right-2 top-1/2 -mt-2 border-b border-l',
        right: '-left-2 top-1/2 -mt-2 border-t border-r'
      }[this.position];
    }
  },
  watch: {
    tooltipContent(newVal) {
      this.hasCloseButton = newVal.length > 100 || newVal.includes('<br') || newVal.includes('<p');
    }
  },
  methods: {
    toggle() {
      if (!this.visible) {
        this.loadTooltip();
      } else {
        this.visible = false;
      }
    },
    async loadTooltip() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get(`/api/tooltips/identifier/${this.identifier}`);
        this.tooltipContent = response.data.content || 'Tooltip content not available';
        this.visible = true;
      } catch (err) {
        console.error('Error loading tooltip:', err);
        this.error = 'Failed to load information';
        this.tooltipContent = '';
        this.visible = true;
      } finally {
        this.loading = false;
      }
    },
    handleClickOutside(event) {
      if (this.visible && !this.$el.contains(event.target)) {
        this.visible = false;
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
}
</script>

<style scoped>
/* Balloon tooltip with smooth animation */
.balloon-tooltip {
  animation: balloonFadeIn 0.2s ease-out;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

@keyframes balloonFadeIn {
  from {
    opacity: 0;
    transform: translateY(5px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Custom scrollbar for long content */
.prose {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 8px;
  line-height: 1.5;
}
.prose::-webkit-scrollbar {
  width: 6px;
}
.prose::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 3px;
}
.prose::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}
.prose::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Balloon pointer styling */
.balloon-tooltip::after {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border: 8px solid transparent;
}
</style>